import React, { useState, useEffect } from "react";
import { FaHeart, FaStar, FaCartPlus, FaChevronLeft } from "react-icons/fa";
import { useParams, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import CircularProgress from "@mui/material/CircularProgress";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const ProductDetails = () => {
  const { id } = useParams();
  const [liked, setLiked] = useState(false);
  const [floatingHearts, setFloatingHearts] = useState([]);
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [relatedLoading, setRelatedLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/products/${id}`);
        setProduct(res.data);
        setLoading(false);

        // Fetch related products after main product is loaded
        fetchRelatedProducts(res.data.category, res.data._id);
      } catch (err) {
        console.error("Failed to fetch product", err);
        setLoading(false);
      }
    };

    const fetchRelatedProducts = async (category, currentId) => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/products`);
        const filtered = res.data.filter(
          (p) => p.category === category && p._id !== currentId
        );
        setRelatedProducts(filtered);
        setRelatedLoading(false);
      } catch (err) {
        console.error("Failed to fetch related products", err);
        setRelatedLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const toggleLike = async (productId) => {
    try {
      const response = await fetch(
        `${SERVER_URL}/api/products/like/${productId}`,
        {
          method: "POST",
        }
      );
      const data = await response.json();
      if (data.success) {
        // Update likes in product object
        setProduct((prev) => ({ ...prev, likes: data.likes }));

        // Show floating heart
        const newHeart = { id: Date.now(), productId };
        setFloatingHearts((prev) => [...prev, newHeart]);

        // Remove heart after animation
        setTimeout(() => {
          setFloatingHearts((prev) => prev.filter((h) => h.id !== newHeart.id));
        }, 1200);

        // Toggle liked state
        setLiked(true);
      }
    } catch (error) {
      console.error("Failed to like product:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center py-20 bg-white">
        <CircularProgress style={{ color: "#eab308" }} size={50} thickness={4} />
        <p className="mt-6 text-gray-500 font-medium tracking-wide">Loading style details...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center py-20 bg-white text-gray-400">
        <p className="text-xl font-medium">This product is no longer available.</p>
        <Link to="/shop" className="mt-6 text-yellow-600 hover:text-yellow-700 font-bold border-b border-yellow-200 hover:border-yellow-600 transition-colors">Return to Shop</Link>
      </div>
    );
  }

  return (
    <>
      {product && (
        <Helmet>
          <title>{`${product.name} | Pak Fashions`}</title>
          <meta
            name="description"
            content={
              product.description?.slice(0, 160) ||
              "Elegant fashion from Pak Fashions"
            }
          />
          <meta
            name="keywords"
            content={`fashion, ${product.category}, ${product.name}, pak fashions`}
          />
          <meta name="robots" content="index, follow" />

          {/* Open Graph */}
          <meta property="og:title" content={product.name} />
          <meta
            property="og:description"
            content={product.description?.slice(0, 160)}
          />
          <meta property="og:image" content={product.image} />
          <meta
            property="og:url"
            content={`https://pakfashions.co.ke/product/${product._id}`}
          />
          <meta property="og:type" content="product" />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content={`${product.name} | Pak Fashions`}
          />
          <meta
            name="twitter:description"
            content={product.description?.slice(0, 160)}
          />
          <meta name="twitter:image" content={product.image} />

          {/* Structured Data (JSON-LD) */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "Product",
              name: product.name,
              image: [product.image],
              description: product.description,
              sku: product._id,
              offers: {
                "@type": "Offer",
                priceCurrency: "KES",
                price: product.price,
                availability: product.inStock ? "InStock" : "OutOfStock",
                url: `https://pakfashions.co.ke/product/${product._id}`,
              },
              brand: {
                "@type": "Brand",
                name: "Pak Fashions",
              },
            })}
          </script>
        </Helmet>
      )}

      {/* Product Section */}
      <section className="bg-white text-gray-900 px-6 py-24 sm:py-32 mt-2 min-h-screen font-sans">
        
        <div className="max-w-7xl mx-auto mb-8">
            <Link
              to="/shop"
              className="inline-flex items-center text-sm font-semibold text-gray-500 hover:text-yellow-600 transition-colors bg-gray-50 hover:bg-yellow-50 px-4 py-2 rounded-full border border-gray-100"
            >
              <FaChevronLeft className="mr-2 w-3 h-3" /> Back to Collection
            </Link>
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-start bg-white shadow-[0_8px_40px_rgb(0,0,0,0.04)] border border-gray-100 rounded-[2.5rem] p-6 sm:p-10 lg:p-14">
          {/* Product Image */}
          <div className="relative overflow-hidden rounded-[2rem] bg-gray-50 group border border-gray-100/50">
            {/* Floating hearts */}
            {floatingHearts
              .filter((h) => h.productId === product._id)
              .map((heart) => (
                <div
                  key={heart.id}
                  className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-yellow-500 text-6xl pointer-events-none z-20"
                  style={{
                    animation: "floatHeart 1.2s ease-out forwards",
                  }}
                >
                  ❤️
                </div>
              ))}
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover aspect-[4/5] sm:aspect-square md:aspect-[4/5] object-top group-hover:scale-105 transition-transform duration-700 ease-in-out"
            />
            {/* Gradient Overlay for luxury feel */}
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/10 to-transparent pointer-events-none"></div>
          </div>

          {/* Product Info */}
          <div className="space-y-8 py-4">
            <div>
              <span className="text-xs font-bold tracking-widest uppercase text-yellow-600 mb-3 block">
                {product.category || "Premium Collection"}
              </span>
              <h1 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 leading-[1.1]">
                {product.name}
              </h1>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <p className="text-3xl font-bold text-gray-900">
                Ksh {product.price.toLocaleString()}
              </p>
              <div className="flex items-center">
                <span
                  className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                    product.inStock
                      ? "bg-green-50 text-green-700 border border-green-200"
                      : "bg-red-50 text-red-700 border border-red-200"
                  }`}
                >
                  {product.inStock ? "In Stock" : "Out of Stock"}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <FaStar
                    key={i}
                    className={`text-lg ${
                      i < (product.rating || 5) ? "text-yellow-400" : "text-gray-200"
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm font-medium text-gray-400 ml-2">
                ({product.rating || "5"} / 5)
              </span>
            </div>
            
            {product.size && (
              <div className="inline-flex items-center gap-3 p-1 pr-4 bg-gray-50 border border-gray-100 rounded-full">
                <div className="bg-white shadow-sm border border-gray-100 rounded-full w-8 h-8 flex items-center justify-center">
                  <span className="text-xs font-bold text-gray-500">SZ</span>
                </div>
                <span className="text-sm font-bold text-gray-900">
                  {product.size}
                </span>
              </div>
            )}

            <div className="prose prose-sm text-gray-500 prose-p:leading-relaxed">
              <p>
                {product.description}
              </p>
            </div>

            <hr className="border-gray-100 border-dashed" />

            {/* Action Buttons */}
            <div className="flex gap-4 pt-2">
              <button
                onClick={() => toggleLike(product._id)}
                title={liked ? "Unlike" : "Like"}
                className={`flex-shrink-0 rounded-full h-14 w-14 text-xl border-2 transition-all duration-300 flex justify-center items-center shadow-sm ${
                  liked
                    ? "bg-red-50 border-red-200 text-red-500 animate-pulse"
                    : "bg-white border-gray-200 text-gray-400 hover:text-yellow-500 hover:border-yellow-200 hover:bg-yellow-50 hover:-translate-y-1"
                }`}
              >
                <FaHeart />
              </button>

              <button
                onClick={() => addToCart(product)}
                disabled={!product.inStock}
                className={`flex-1 flex items-center justify-center gap-3 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-md ${
                  product.inStock
                    ? "bg-gray-900 text-white hover:bg-black hover:shadow-xl hover:-translate-y-1 hover:shadow-gray-900/20"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                }`}
              >
                <FaCartPlus className="w-5 h-5" />
                {product.inStock ? "Add to Cart" : "Sold Out"}
              </button>
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-32 max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h3 className="text-2xl font-black text-gray-900 tracking-tight mb-2">
                Complete the Look
              </h3>
              <p className="text-gray-500 font-medium">Curated pieces you might also love.</p>
            </div>
            <div className="hidden sm:block h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent ml-8"></div>
          </div>
          
          {relatedLoading ? (
            <div className="flex justify-center py-20 opacity-70">
              <CircularProgress style={{ color: "#eab308" }} size={40} thickness={4} />
            </div>
          ) : relatedProducts.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-3xl border border-gray-100 text-gray-500">
              <p className="font-medium">No related items found at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.slice(0, 4).map((p) => (
                <Link
                  to={`/product/${p._id}`}
                  key={p._id}
                  className="group bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:border-yellow-200 transition-all duration-300 p-3 sm:p-4 hover:-translate-y-1 flex flex-col"
                >
                  <div className="relative h-48 sm:h-64 mb-4 rounded-xl overflow-hidden bg-gray-50">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="h-full w-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="mt-auto px-1">
                    <h4 className="text-base font-bold text-gray-900 truncate group-hover:text-yellow-600 transition-colors">
                      {p.name}
                    </h4>
                    <p className="text-sm text-gray-500 font-medium mt-1">
                      Ksh {p.price.toLocaleString()}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default ProductDetails;
